export type MembershipAccountType = 'BANK' | 'UPI';
export type MembershipMemberTypeKind = 'INDIVIDUAL' | 'COUPLE' | 'FAMILY' | 'GROUP';
export type MembershipFeeKind = 'REGISTRATION' | 'MEMBERSHIP' | 'RENEWAL' | 'ONE_TIME';
export type MembershipFeePeriodicity = 'MONTHLY' | 'QUARTERLY' | 'YEARLY' | 'LIFETIME' | 'CUSTOM';
export type MembershipCycleAnchor = 'JOIN_ANNIVERSARY' | 'FIXED_MONTH';
export type MembershipRenewalFeeMode = 'SAME_AS_MEMBERSHIP' | 'CUSTOM';
export type IssuedMembershipStatus = 'PENDING' | 'ACTIVE' | 'EXPIRED' | 'REJECTED' | 'CANCELLED';
export type MembershipPaymentMode = 'MANUAL_BANK' | 'MANUAL_UPI';
export type MembershipPaymentDeclarationStatus =
  | 'DECLARED'
  | 'PARTIALLY_VERIFIED'
  | 'VERIFIED'
  | 'REJECTED';

export interface CommunityPaymentAccount {
  id: string;
  communityId: string;
  name: string;
  type: MembershipAccountType;
  isActive: boolean;
  beneficiaryName?: string | null;
  bankName?: string | null;
  accountNumber?: string | null;
  ifscCode?: string | null;
  branchName?: string | null;
  upiId?: string | null;
  upiType?: string | null;
  upiName?: string | null;
  upiAccountNumber?: string | null;
  upiQrCode?: string | null;
}

export interface MembershipInstallmentComponentSplit {
  id?: string;
  installmentItemId?: string;
  feeComponentId: string;
  amount: number;
}

export interface MembershipInstallmentItem {
  id?: string;
  installmentPackageId?: string;
  name: string;
  installmentNumber: number;
  payableStartDate?: string | null;
  payableEndDate: string;
  componentSplits: MembershipInstallmentComponentSplit[];
}

export interface MembershipInstallmentPackage {
  id?: string;
  feeBucketId?: string;
  name: string;
  description?: string | null;
  sortOrder: number;
  isActive?: boolean;
  installments: MembershipInstallmentItem[];
}

export interface MembershipFeeComponent {
  id: string;
  feeBucketId?: string;
  paymentAccountId: string;
  name: string;
  amount: number;
  sortOrder: number;
}

export interface MembershipFeeBucket {
  id?: string;
  memberTypeId?: string;
  feeKind: MembershipFeeKind;
  isEnabled: boolean;
  totalAmount: number;
  sortOrder: number;
  components: MembershipFeeComponent[];
  installmentPackages: MembershipInstallmentPackage[];
}

export interface MembershipMemberType {
  id: string;
  feeStructureId?: string;
  name: string;
  feeType: MembershipMemberTypeKind;
  renewalFeeMode?: MembershipRenewalFeeMode;
  sortOrder: number;
  isActive: boolean;
  feeBuckets: MembershipFeeBucket[];
}

export interface MembershipFeeStructure {
  id: string;
  communityId: string;
  name: string;
  applicableFrom: string;
  isActive: boolean;
  feePeriodicity?: MembershipFeePeriodicity;
  cycleAnchor?: MembershipCycleAnchor;
  fixedCycleMonth?: number | null;
  graceDays?: number;
  memberTypes: MembershipMemberType[];
}

export interface MembershipPaymentAllocation {
  id: string;
  paymentDeclarationId?: string;
  feeComponentId: string;
  installmentSplitId?: string | null;
  amount: number;
}

export interface MembershipPaymentDeclaration {
  id: string;
  issuedMembershipId: string;
  installmentItemId?: string | null;
  declaredByUserId: string;
  paymentMode: MembershipPaymentMode;
  amountDeclared: number;
  amountAccepted?: number | null;
  outstandingBalance: number;
  declaredAt: string;
  paidAt?: string | null;
  referenceNumber?: string | null;
  proofUrl?: string | null;
  notes?: string | null;
  status: MembershipPaymentDeclarationStatus;
  verifiedAt?: string | null;
  verifiedByUserId?: string | null;
  allocations: MembershipPaymentAllocation[];
}

export interface IssuedMembership {
  id: string;
  communityId: string;
  userId: string;
  feeStructureId: string;
  memberTypeId: string;
  selectedFeeKind: MembershipFeeKind;
  installmentPackageId?: string | null;
  status: IssuedMembershipStatus;
  joinDate: string;
  membershipStartDate?: string | null;
  membershipEndDate?: string | null;
  isLifetime: boolean;
  notes?: string | null;
  verifiedAt?: string | null;
  verifiedByUserId?: string | null;
  firstName?: string | null;
  lastName?: string | null;
  email?: string | null;
  phone?: string | null;
  image?: string | null;
  paymentDeclarations?: MembershipPaymentDeclaration[];
}

export const MEMBER_TYPE_LABELS: Record<MembershipMemberTypeKind, string> = {
  INDIVIDUAL: 'Individual',
  COUPLE: 'Couple',
  FAMILY: 'Family',
  GROUP: 'Group',
};

export const FEE_KIND_LABELS: Record<MembershipFeeKind, string> = {
  REGISTRATION: 'Registration fee',
  MEMBERSHIP: 'Membership fee',
  RENEWAL: 'Renewal fee',
  ONE_TIME: 'One-time lifetime fee',
};

export const FEE_PERIODICITY_LABELS: Record<MembershipFeePeriodicity, string> = {
  MONTHLY: 'Monthly',
  QUARTERLY: 'Quarterly',
  YEARLY: 'Yearly',
  LIFETIME: 'Lifetime',
  CUSTOM: 'Custom / manual',
};

export const CYCLE_ANCHOR_LABELS: Record<MembershipCycleAnchor, string> = {
  JOIN_ANNIVERSARY: 'Month of joining',
  FIXED_MONTH: 'Specific month',
};

export const RENEWAL_FEE_MODE_LABELS: Record<MembershipRenewalFeeMode, string> = {
  SAME_AS_MEMBERSHIP: 'Same as membership fee',
  CUSTOM: 'Define separately',
};

export const ISSUED_MEMBERSHIP_STATUS_LABELS: Record<IssuedMembershipStatus, string> = {
  PENDING: 'Pending',
  ACTIVE: 'Active',
  EXPIRED: 'Expired',
  REJECTED: 'Rejected',
  CANCELLED: 'Cancelled',
};

export const PAYMENT_DECLARATION_STATUS_LABELS: Record<MembershipPaymentDeclarationStatus, string> =
  {
    DECLARED: 'Declared',
    PARTIALLY_VERIFIED: 'Partially verified',
    VERIFIED: 'Verified',
    REJECTED: 'Rejected',
  };

export const PAYMENT_MODE_LABELS: Record<MembershipPaymentMode, string> = {
  MANUAL_BANK: 'Bank transfer',
  MANUAL_UPI: 'UPI',
};

export function formatMembershipPrice(amount: number, currency = 'INR'): string {
  if (currency === 'INR') {
    return `₹${amount.toLocaleString('en-IN')}`;
  }
  return `${currency} ${amount.toLocaleString()}`;
}

export function toDateInputValue(value: string | Date | null | undefined): string {
  if (!value) return '';
  const d = typeof value === 'string' ? new Date(value) : value;
  if (Number.isNaN(d.getTime())) return '';
  return d.toISOString().slice(0, 10);
}

export function formatMembershipDate(date: string | null | undefined): string {
  if (!date) return '—';
  return new Date(date).toLocaleDateString(undefined, {
    year: 'numeric',
    month: 'short',
    day: 'numeric',
  });
}

export function getMembershipExpiryLabel(
  membershipEndDate: string | null | undefined
): string | null {
  if (!membershipEndDate) return null;
  const expiry = new Date(membershipEndDate);
  const now = new Date();
  const diffMs = expiry.getTime() - now.getTime();
  const diffDays = Math.ceil(diffMs / (1000 * 60 * 60 * 24));

  if (diffDays < 0) {
    return `Expired ${formatMembershipDate(membershipEndDate)}`;
  }
  if (diffDays === 0) {
    return 'Expires today';
  }
  if (diffDays <= 30) {
    return `Expires in ${diffDays} day${diffDays === 1 ? '' : 's'}`;
  }
  return `Valid until ${formatMembershipDate(membershipEndDate)}`;
}

export function getFeeBucketTotal(bucket: MembershipFeeBucket): number {
  return bucket.components.reduce((sum, component) => sum + component.amount, 0);
}

export function getInstallmentTotal(installment: MembershipInstallmentItem): number {
  return installment.componentSplits.reduce((sum, split) => sum + split.amount, 0);
}

export function getPaymentAccountLabel(account: CommunityPaymentAccount): string {
  return account.type === 'BANK'
    ? `${account.name} (${account.bankName ?? 'Bank'})`
    : `${account.name} (${account.upiId ?? 'UPI'})`;
}

export function isBucketPayableNow(
  installment: MembershipInstallmentItem,
  now = new Date()
): boolean {
  const today = now.toISOString().slice(0, 10);
  const start = installment.payableStartDate ?? null;
  return (!start || start <= today) && installment.payableEndDate >= today;
}

export function createClientMembershipId(): string {
  return Math.random().toString(36).slice(2, 12).padEnd(10, '0').slice(0, 10);
}
